import type { TimelineData, TimelineItem } from '@/features/timeline/model/schema';
import type { TimelineRecord } from '@/features/timeline/model/timeline-record';
import { appAnalytics } from '@/shared/analytics';
import { TimelineConflictError } from './timeline-errors';
import {
  createTimelineCommand,
  type TimelineCommand,
} from './timeline-editor-command';
import {
  DEFAULT_HISTORY_LIMIT,
  TimelineEditorSession,
} from './timeline-editor-session';
import type {
  TimelineCommandDto,
  TimelineCommandRequest,
  TimelineEditorState,
} from './timeline-editor-types';
import { generateScopedId } from './timeline-id';

const assertNever = (value: never): never => {
  throw new Error(`Unsupported timeline command request: ${JSON.stringify(value)}`);
};

const normalizeDraftItem = (item: {
  id?: string;
  title: string;
  startDate: string;
  endDate?: string;
}): TimelineItem => {
  const id = item.id && item.id.length > 0 ? item.id : generateScopedId('item');
  return {
    id,
    title: item.title,
    startDate: item.startDate,
    ...(item.endDate ? { endDate: item.endDate } : {}),
  };
};

const generateNextSectionLabel = (sections: TimelineData['pages'][number]['sections']): string => {
  const sectionLabelRegex = /^Section (\d+)$/;
  const maxIndex = sections.reduce((max, section) => {
    const matched = section.label.match(sectionLabelRegex);
    if (!matched) {
      return max;
    }
    const parsed = Number(matched[1]);
    if (!Number.isFinite(parsed)) {
      return max;
    }
    return Math.max(max, parsed);
  }, 0);
  return `Section ${maxIndex + 1}`;
};

interface TimelineEditorPersistence {
  findById(id: string): Promise<TimelineRecord | null>;
  applyChanges(
    id: string,
    input: {
      changes: TimelineCommandDto[];
    },
  ): Promise<TimelineRecord>;
}

export class TimelineEditorUseCases {
  private readonly sessions = new Map<string, TimelineEditorSession>();

  constructor(
    private readonly persistence: TimelineEditorPersistence,
    private readonly historyLimit = DEFAULT_HISTORY_LIMIT,
  ) {}

  async createEditorSession(id: string): Promise<TimelineEditorState | null> {
    const record = await this.persistence.findById(id);
    if (!record) {
      this.sessions.delete(id);
      return null;
    }

    const session = new TimelineEditorSession(id, record.data, this.historyLimit);
    this.sessions.set(id, session);

    const sections = record.data.pages[0].sections;
    appAnalytics.track('timeline_opened', {
      timeline_id: id,
      version: record.version,
      section_count: sections.length,
      item_count: sections.reduce((sum, s) => sum + s.items.length, 0),
    });

    return session.toState();
  }

  async refreshEditorSession(id: string): Promise<TimelineEditorState | null> {
    const existing = this.sessions.get(id);
    if (!existing) {
      return this.createEditorSession(id);
    }

    const record = await this.persistence.findById(id);
    if (!record) {
      this.sessions.delete(id);
      return null;
    }

    return existing.replaceCurrent(record.data);
  }

  disposeEditorSession(id: string): void {
    this.sessions.delete(id);
  }

  disposeAllEditorSessions(): void {
    this.sessions.clear();
  }

  async executeCommand(
    id: string,
    request: TimelineCommandRequest,
    onOptimistic?: (state: TimelineEditorState) => void,
  ): Promise<TimelineEditorState | null> {
    const session = await this.ensureEditorSession(id);
    if (!session) {
      return null;
    }

    const input = this.normalizeCommandInput(request, session.getCurrentData());
    const command = createTimelineCommand(input);

    const result = await this.persistEditorCommand(id, session, command, input, onOptimistic);

    if (result) {
      const editTypeMap: Record<string, string> = {
        'add-section': 'add_section',
        'remove-section': 'remove_section',
        'move-section': 'move_section',
        'update-section-label': 'update_section_label',
        'update-item-title': 'update_item_title',
        'create-item': 'create_item',
        'change-item-date-range': 'change_item_date_range',
        'hide-section': 'hide_section',
        'show-section': 'show_section',
        'delete-item': 'delete_item',
        'replace-snapshot': 'replace_snapshot',
      };

      appAnalytics.track('timeline_edit_completed', {
        timeline_id: id,
        edit_type: editTypeMap[input.type] ?? input.type,
        version: '2.0',
      });
    }

    return result;
  }

  async addSection(id: string, onOptimistic?: (state: TimelineEditorState) => void): Promise<TimelineEditorState | null> {
    return this.executeCommand(id, { type: 'add-section' }, onOptimistic);
  }

  async removeSection(id: string, sectionId: string, onOptimistic?: (state: TimelineEditorState) => void): Promise<TimelineEditorState | null> {
    return this.executeCommand(id, { type: 'remove-section', sectionId }, onOptimistic);
  }

  async moveSection(id: string, sectionId: string, toIndex: number, onOptimistic?: (state: TimelineEditorState) => void): Promise<TimelineEditorState | null> {
    return this.executeCommand(id, { type: 'move-section', sectionId, toIndex }, onOptimistic);
  }

  async updateSectionLabel(
    id: string,
    sectionId: string,
    nextLabel: string,
    onOptimistic?: (state: TimelineEditorState) => void,
  ): Promise<TimelineEditorState | null> {
    return this.executeCommand(id, {
      type: 'update-section-label',
      sectionId,
      nextLabel,
    }, onOptimistic);
  }

  async updateItemTitle(
    id: string,
    sectionId: string,
    itemId: string,
    nextTitle: string,
    onOptimistic?: (state: TimelineEditorState) => void,
  ): Promise<TimelineEditorState | null> {
    return this.executeCommand(id, {
      type: 'update-item-title',
      sectionId,
      itemId,
      nextTitle,
    }, onOptimistic);
  }

  async createItem(
    id: string,
    sectionId: string,
    draft: {
      title: string;
      startDate: string;
      endDate?: string;
    },
    onOptimistic?: (state: TimelineEditorState) => void,
  ): Promise<TimelineEditorState | null> {
    return this.executeCommand(id, {
      type: 'create-item',
      sectionId,
      item: draft,
    }, onOptimistic);
  }

  async changeItemDateRange(
    id: string,
    itemId: string,
    nextRange: {
      sectionId: string;
      startDate: string;
      endDate?: string;
    },
    onOptimistic?: (state: TimelineEditorState) => void,
  ): Promise<TimelineEditorState | null> {
    return this.executeCommand(id, {
      type: 'change-item-date-range',
      itemId,
      nextRange,
    }, onOptimistic);
  }

  async replaceWithSnapshot(
    id: string,
    snapshot: TimelineData,
    onOptimistic?: (state: TimelineEditorState) => void,
  ): Promise<TimelineEditorState | null> {
    return this.executeCommand(id, { type: 'replace-snapshot', snapshot }, onOptimistic);
  }

  async hideSection(id: string, sectionId: string, onOptimistic?: (state: TimelineEditorState) => void): Promise<TimelineEditorState | null> {
    return this.executeCommand(id, { type: 'hide-section', sectionId }, onOptimistic);
  }

  async showSection(id: string, sectionId: string, onOptimistic?: (state: TimelineEditorState) => void): Promise<TimelineEditorState | null> {
    return this.executeCommand(id, { type: 'show-section', sectionId }, onOptimistic);
  }

  async deleteItem(id: string, sectionId: string, itemId: string, onOptimistic?: (state: TimelineEditorState) => void): Promise<TimelineEditorState | null> {
    return this.executeCommand(id, {
      type: 'delete-item',
      sectionId,
      itemId,
    }, onOptimistic);
  }

  async undo(id: string, onOptimistic?: (state: TimelineEditorState) => void): Promise<TimelineEditorState | null> {
    const session = await this.ensureEditorSession(id);
    if (!session) {
      return null;
    }

    try {
      const result = await session.undo((input) => {
        return this.persistence.applyChanges(id, input).then((r) => r.data);
      }, onOptimistic);

      if (result) {
        appAnalytics.track('timeline_undo_completed', {
          timeline_id: id,
          version: '2.0',
        });
      }

      return result;
    } catch (error) {
      return this.handleConflict(id, error);
    }
  }

  async redo(id: string, onOptimistic?: (state: TimelineEditorState) => void): Promise<TimelineEditorState | null> {
    const session = await this.ensureEditorSession(id);
    if (!session) {
      return null;
    }

    try {
      const result = await session.redo((input) => {
        return this.persistence.applyChanges(id, input).then((r) => r.data);
      }, onOptimistic);

      if (result) {
        appAnalytics.track('timeline_redo_completed', {
          timeline_id: id,
          version: '2.0',
        });
      }

      return result;
    } catch (error) {
      return this.handleConflict(id, error);
    }
  }

  private normalizeCommandInput(
    request: TimelineCommandRequest,
    current: TimelineData,
  ): TimelineCommandDto {
    switch (request.type) {
      case 'add-section': {
        const sectionId = request.sectionId && request.sectionId.length > 0 ? request.sectionId : generateScopedId('timeline');
        const label = request.label?.trim();
        return {
          type: 'add-section',
          sectionId,
          label: label && label.length > 0 ? label : generateNextSectionLabel(current.pages[0].sections),
        };
      }
      case 'create-item':
        return {
          type: 'create-item',
          sectionId: request.sectionId,
          item: normalizeDraftItem(request.item),
        };
      case 'update-section-label':
      case 'update-item-title':
      case 'delete-item':
      case 'remove-section':
      case 'move-section':
      case 'hide-section':
      case 'show-section':
      case 'replace-snapshot':
        return request;
      case 'change-item-date-range':
        return request;
      default:
        return assertNever(request);
    }
  }

  private async ensureEditorSession(id: string): Promise<TimelineEditorSession | null> {
    const existing = this.sessions.get(id);
    if (existing) {
      return existing;
    }

    const record = await this.persistence.findById(id);
    if (!record) {
      return null;
    }

    const session = new TimelineEditorSession(id, record.data, this.historyLimit);
    this.sessions.set(id, session);
    return session;
  }

  private async persistEditorCommand(
    id: string,
    session: TimelineEditorSession,
    command: TimelineCommand,
    input: TimelineCommandDto,
    onOptimistic?: (state: TimelineEditorState) => void,
  ): Promise<TimelineEditorState | null> {
    try {
      return await session.execute(command, input, (payload) => {
        return this.persistence.applyChanges(id, payload).then((r) => r.data);
      }, onOptimistic);
    } catch (error) {
      return this.handleConflict(id, error);
    }
  }

  private async handleConflict(id: string, error: unknown): Promise<never> {
    if (!(error instanceof TimelineConflictError)) {
      throw error;
    }

    const latest = await this.persistence.findById(id);
    if (!latest) {
      throw error;
    }

    const session = new TimelineEditorSession(id, latest.data, this.historyLimit);
    this.sessions.set(id, session);

    throw new TimelineConflictError(session.toState());
  }
}
